
import { initializeApp } from "firebase/app";
import {
  isSupported as isMessagingSupported,
  getMessaging,
  getToken,
  onMessage,
  type Messaging
} from "firebase/messaging";
import { logger } from "./services/logger";

// Firebase config from env
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY!,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN!,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID!,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID!,
  appId: process.env.REACT_APP_FIREBASE_APP_ID!,
};
const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY!;

const app = initializeApp(firebaseConfig);

// Globals we only set when supported
let messaging: Messaging | null = null;
let swRegistration: ServiceWorkerRegistration | null = null;

// Helper: is this environment able to do Web Push?
async function canUseWebPush(): Promise<boolean> {
  try {
    // Modular SDK support check (prevents messaging/unsupported-browser throws)
    const supported = await isMessagingSupported();
    if (!supported) return false;

    // Feature presence (iOS Chrome tabs fail here)
    const features =
      "serviceWorker" in navigator &&
      "Notification" in window &&
      "PushManager" in window;

    if (!features) return false;

    // HTTPS or localhost only
    if (
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost"
    ) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

// Register SW only when we actually need it and it’s supported
async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;

  if (swRegistration) return swRegistration;
  try {
    const reg =
      (await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js")) ||
      (await navigator.serviceWorker.register("/firebase-messaging-sw.js"));
    swRegistration = reg;
    return reg;
  } catch (err) {
    logger.error("FCM: SW registration failed", err);
    return null;
  }
}

// Public: get token (safe on all browsers)
export async function getFcmToken(): Promise<string | undefined> {
  const ok = await canUseWebPush();
  if (!ok) {
    logger.info("[FCM] Web Push not supported here (likely iOS Chrome tab).");
    return undefined;
  }

  try {
    // Create messaging ONLY after isSupported() above
    messaging ||= getMessaging(app);

    // Ask permission (avoid calling in iOS Chrome tabs; we already gated)
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      logger.info(`[FCM] Permission = ${perm}`);
      return undefined;
    }

    const reg = await ensureServiceWorker();
    if (!reg) {
      logger.error("FCM: No service worker registration");
      return undefined;
    }

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: reg,
    });
    if (!token) {
      logger.error("FCM: getToken returned empty");
      return undefined;
    }
    return token;
  } catch (err) {
    logger.error("FCM token error:", err);
    return undefined;
  }
}

// Foreground messages: only attach when supported
(async () => {
  if (await canUseWebPush()) {
    try {
      messaging ||= getMessaging(app);
      onMessage(messaging, (payload) => {
        console.log("FCM: Foreground message", payload);
        const n = payload.notification || {};
        const url = payload.data?.url;

        if (Notification.permission === "granted" && n) {
          let msg = (n.title || "SRF Finder") + "\n\n" + (n.body || "");
          if (url) msg += `\n\nURL: ${url}`;
          alert(msg);
        }
      });
    } catch (err) {
      // Never let this crash the app
      console.warn("[FCM] onMessage setup failed:", err);
    }
  } else {
    console.info("[FCM] Skipping onMessage listener; unsupported env.");
  }
})();

// (Optional) one-off utility; won’t run automatically
declare global {
  interface Window {
    getFcmToken?: () => Promise<string | undefined>;
    unregisterAllServiceWorkers?: () => Promise<void>;
  }
}
window.getFcmToken = getFcmToken;
window.unregisterAllServiceWorkers = async () => {
  if (!("serviceWorker" in navigator)) return;
  const regs = await navigator.serviceWorker.getRegistrations();
  for (const r of regs) await r.unregister();
  console.log("All SWs unregistered.");
};
